import { GifReader, type Frame } from "omggif";

export interface ExtractedGifFrame {
  frameInfo: Frame;
  getImage: () => HTMLCanvasElement;
}

const clearFrameRect = (
  pixels: Uint8ClampedArray,
  canvasWidth: number,
  frameInfo: Frame
) => {
  for (let y = frameInfo.y; y < frameInfo.y + frameInfo.height; y += 1) {
    const start = (y * canvasWidth + frameInfo.x) * 4;
    const end = start + frameInfo.width * 4;
    pixels.fill(0, start, end);
  }
};

const pixelsToCanvas = (
  pixels: Uint8ClampedArray,
  width: number,
  height: number
) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not create GIF frame canvas context");
  }

  context.putImageData(
    new ImageData(pixels as ImageDataArray, width, height),
    0,
    0
  );
  return canvas;
};

const getGifBytes = async (url: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Could not fetch GIF: ${response.status}`);
  }

  return new Uint8Array(await response.arrayBuffer());
};

// omggif's decodeAndBlitFrameRGBA writes only into the frame's local sub-rect
// and skips transparent pixels — frames beyond the first are deltas. Composite
// across iterations with disposal-method handling so each frame is a complete
// visible image.
export const extractGifFrameCanvases = async (
  url: string
): Promise<ExtractedGifFrame[]> => {
  const reader = new GifReader(await getGifBytes(url));
  const { width, height } = reader;
  const frames: ExtractedGifFrame[] = [];

  const pixels = new Uint8ClampedArray(width * height * 4);
  let previousPixels: Uint8ClampedArray | null = null;
  let previousFrameInfo: Frame | null = null;

  for (let frameIndex = 0; frameIndex < reader.numFrames(); frameIndex += 1) {
    const frameInfo = reader.frameInfo(frameIndex);

    if (previousFrameInfo?.disposal === 2) {
      clearFrameRect(pixels, width, previousFrameInfo);
    } else if (previousFrameInfo?.disposal === 3 && previousPixels) {
      pixels.set(previousPixels);
    }

    previousPixels =
      frameInfo.disposal === 3 ? new Uint8ClampedArray(pixels) : null;

    reader.decodeAndBlitFrameRGBA(frameIndex, pixels);

    const framePixels = new Uint8ClampedArray(pixels);
    frames.push({
      frameInfo,
      getImage: () => pixelsToCanvas(framePixels, width, height)
    });

    previousFrameInfo = frameInfo;
  }

  return frames;
};
